import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, Copy, KeyRound, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

// REVEAL-ONCE. A write key exists in cleartext only in its create response — the server stores a hash
// and never returns it again. This dialog is that one moment: it holds the raw key for the length of
// one open dialog and lets the user copy it. Shared by Settings (rotation) and Install (onboarding) so
// there is exactly one copy of the "copy it now" UX.
export function RevealKeyDialog({
  value,
  onClose,
  title = 'Copy your write key now',
  description = 'This is the only time this key will ever be shown. We store a hash of it — if you lose it, revoke it and create another.',
}: {
  /** The raw key to reveal, or null when the dialog is closed. */
  value: string | null;
  onClose: () => void;
  title?: string;
  description?: string;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value ?? '');
      setCopied(true);
      toast.success('SDK key copied');
    } catch {
      toast.error('Could not copy the key. Select it and copy manually.');
    }
  };

  return (
    <Dialog
      open={value !== null}
      onOpenChange={(open) => {
        if (!open && copied) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <DialogContent
        hideClose={!copied}
        className="sm:max-w-xl"
        onEscapeKeyDown={(event) => {
          if (!copied) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (!copied) event.preventDefault();
        }}
      >
        <DialogHeader>
          <span className="mb-2 grid size-10 place-items-center rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-300">
            <KeyRound className="size-5" />
          </span>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border bg-secondary/40 p-2 pl-3">
            <code className="min-w-0 flex-1 select-all overflow-x-auto whitespace-nowrap font-mono text-xs">
              {value}
            </code>
            <Button variant={copied ? 'secondary' : 'default'} size="sm" aria-label="Copy write key" onClick={copy}>
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? 'Copied' : 'Copy key'}
            </Button>
          </div>

          <div className="flex gap-2 rounded-lg bg-amber-500/10 px-3 py-2.5 text-xs leading-5 text-amber-800 dark:text-amber-200">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            Keep this key in your secrets manager. You won’t be able to reveal it again after continuing.
          </div>
        </div>

        <DialogFooter>
          <Button
            disabled={!copied}
            onClick={() => {
              setCopied(false);
              onClose();
            }}
          >
            Continue to install
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
